import { useState } from "preact/hooks";
import { useRoute } from "preact-iso";
import { UserPlusIcon } from "@heroicons/react/24/outline";
import { api } from "../lib/api-client";
import { authClient } from "../lib/auth-client";
import { ApiError } from "../lib/api-client";
import { useAsyncData } from "../lib/use-async-data";
import { canManageProject } from "../lib/permissions";
import { useConfirm } from "../lib/use-confirm";
import { useToast } from "../lib/use-toast";
import {
  asRateLimitPayload,
  inviteRateLimitMessage,
} from "../lib/rate-limit-message";
import { OwnerBanner } from "../components/owner-banner";
import { UserList } from "../components/user-list";
import {
  Alert,
  Button,
  Card,
  CardBody,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  SkeletonList,
} from "../components/ui";
import type { ProjectMemberDto, ProjectMemberRole } from "@shared/types";

export function ProjectMembersPage() {
  const { params } = useRoute();
  const projectId = params.projectId!;

  const { data, error, reload, setError } = useAsyncData(
    () =>
      Promise.all([
        api.getProject(projectId),
        api.listMembers(projectId),
      ]).then(([p, list]) => ({ project: p, items: list.items })),
    [projectId],
  );
  const project = data?.project ?? null;
  const items = data?.items ?? null;

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [inviteRole, setInviteRole] = useState<ProjectMemberRole>("member");
  const [submitting, setSubmitting] = useState(false);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);

  const { confirm, dialog: confirmDialog } = useConfirm();
  const toast = useToast();

  const canManage = canManageProject(project?.viewerRole);
  // Used only to word the self-demotion confirm. A platform admin is not a
  // Better Auth user, so under /platform-admin this stays undefined.
  const { data: session } = authClient.useSession();
  const sessionUserId =
    project?.viewerRole === "platform_admin" ? undefined : session?.user.id;

  async function onInvite(e: Event) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.inviteMember(projectId, {
        email,
        name: name || undefined,
        role: inviteRole,
      });
      const target = email;
      setEmail("");
      setName("");
      setInviteRole("member");
      reload();
      if (!res.emailSent) {
        // "Press invite again" is the right advice for a transient failure and
        // the wrong advice when the sending quota is exhausted — the retry
        // fails and spends this address's invite rate limit for nothing.
        toast.warning(
          res.emailErrorCode === "quota_exhausted"
            ? `メールの送信枠を使い切っています。${target} のメンバーシップは登録済みなので、枠が回復してからもう一度「招待する」を押してください。`
            : `${target} へのメール送信に失敗しました。メンバーシップは登録済みなので、もう一度「招待する」を押すと magic link を再送します。`,
        );
      } else if (res.alreadyMember) {
        toast.success(`${target} は既にメンバーです。magic link を再送しました。`, {
          duration: 4000,
        });
      } else if (res.isNewUser) {
        toast.success(`${target} に招待メールを送信しました。`);
      } else {
        toast.success(
          `${target} をメンバーに追加し、magic link を送信しました。`,
          { duration: 4000 },
        );
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        setError(inviteRateLimitMessage(asRateLimitPayload(e.payload)));
        return;
      }
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onRoleChange(
    member: ProjectMemberDto,
    role: ProjectMemberRole,
    select: HTMLSelectElement,
  ) {
    if (member.role === role) return;
    // The Select is controlled by `member.role`, but a change that doesn't
    // update state never re-renders, so the DOM would keep showing the
    // rejected value. Put it back explicitly on cancel / failure.
    const revert = () => {
      select.value = member.role;
    };
    if (member.role === "owner" && role === "member") {
      const isSelf = sessionUserId !== undefined && member.userId === sessionUserId;
      const ok = await confirm(
        isSelf
          ? {
              title: "自分を member に降格しますか？",
              description:
                "降格すると、このプロジェクトの設定変更・デプロイ・API キー管理・メンバー管理ができなくなります。元に戻すには別の owner に依頼する必要があります。",
              confirmLabel: "降格する",
              tone: "danger",
            }
          : {
              title: `${member.email} を member に降格しますか？`,
              description:
                "降格すると、このメンバーはプロジェクトの設定変更・デプロイ・API キー管理・メンバー管理ができなくなります。",
              confirmLabel: "降格する",
              tone: "danger",
            },
      );
      if (!ok) {
        revert();
        return;
      }
    }
    setUpdatingRole(member.userId);
    setError(null);
    try {
      await api.updateMemberRole(projectId, member.userId, role);
      toast.success(`${member.email} を ${role} に変更しました。`);
      reload();
    } catch (e) {
      revert();
      if (e instanceof ApiError && e.status === 409) {
        const code = (e.payload as { error?: string } | null)?.error;
        if (code === "last_owner_protected") {
          setError("最後の owner は降格できません。先に別の owner を追加してください。");
          return;
        }
      }
      setError((e as Error).message);
    } finally {
      setUpdatingRole(null);
    }
  }

  async function onRemove(m: ProjectMemberDto) {
    const ok = await confirm({
      title: `${m.email} をプロジェクトから外しますか？`,
      description:
        "このメンバーは記事・画像・設定にアクセスできなくなります。ユーザー自体は削除されません。",
      confirmLabel: "外す",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.removeMember(projectId, m.userId);
      reload();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const code = (e.payload as { error?: string } | null)?.error;
        if (code === "last_owner_protected") {
          setError(
            "最後の owner はこの画面からは外せません。owner の変更が必要な場合は管理者にお問い合わせください。",
          );
          return;
        }
      }
      setError((e as Error).message);
    }
  }

  return (
    <section class="space-y-8">
      {confirmDialog}
      <PageHeader
        title="メンバー"
        description={
          project ? <span class="font-mono">/{project.slug}</span> : "..."
        }
      />

      <OwnerBanner project={project} />

      {error && <Alert tone="danger">{error}</Alert>}

      {canManage && (
        <Card>
          <CardBody>
            <form onSubmit={onInvite} class="grid gap-3 sm:grid-cols-4">
              <Field label="メールアドレス" required class="sm:col-span-2">
                <Input
                  type="email"
                  required
                  value={email}
                  onInput={(e) =>
                    setEmail((e.target as HTMLInputElement).value)
                  }
                />
              </Field>
              <Field label="名前 (任意)">
                <Input
                  type="text"
                  value={name}
                  onInput={(e) =>
                    setName((e.target as HTMLInputElement).value)
                  }
                />
              </Field>
              {/* Shown to every owner, not just platform admins: the API has
                  always accepted `role` from any project owner, and hiding the
                  control only made owners unable to appoint a second owner
                  through the UI they were given. An owner can already delete
                  the whole project, so this is not an escalation. */}
              <Field label="役割">
                <Select
                  value={inviteRole}
                  onChange={(e) =>
                    setInviteRole(
                      (e.currentTarget as HTMLSelectElement).value as ProjectMemberRole,
                    )
                  }
                >
                  <option value="member">member</option>
                  <option value="owner">owner</option>
                </Select>
              </Field>
              <div class="flex items-center justify-end sm:col-span-4">
                <Button
                  type="submit"
                  variant="primary"
                  iconLeft={<UserPlusIcon class="h-4 w-4" />}
                  loading={submitting}
                  disabled={submitting}
                >
                  {submitting ? "送信中…" : "招待する"}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {items === null ? (
        <SkeletonList count={2} rowClass="h-12" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<UserPlusIcon class="h-5 w-5" />}
          title="メンバーがいません"
          description="上のフォームから招待してください。"
        />
      ) : (
        <UserList
          items={items}
          getKey={(member) => member.userId}
          getEmail={(member) => member.email}
          getName={(member) => member.name}
          getRole={(member) => member.role}
          getRoleTone={(member) =>
            member.role === "owner" ? "warning" : "neutral"
          }
          getEmailVerified={(member) => member.emailVerified}
          renderRole={
            canManage
              ? (member, layout) => (
                  <Select
                    aria-label={`${member.email} の役割`}
                    value={member.role}
                    disabled={updatingRole === member.userId}
                    onChange={(e) => {
                      const select = e.currentTarget as HTMLSelectElement;
                      void onRoleChange(
                        member,
                        select.value as ProjectMemberRole,
                        select,
                      );
                    }}
                    class={layout === "desktop" ? "min-w-28" : "w-32"}
                  >
                    <option value="member">member</option>
                    <option value="owner">owner</option>
                  </Select>
                )
              : undefined
          }
          action={
            canManage
              ? {
                  desktopKind: "icon",
                  desktopLabel: "削除",
                  mobileLabel: "外す",
                  ariaLabel: "削除",
                  onAction: onRemove,
                }
              : undefined
          }
        />
      )}
    </section>
  );
}
